/**
 * Theme provider — picks one of seven IDE-inspired palettes,
 * persists the choice to localStorage, and sets the
 * ``data-theme`` attribute on ``<html>`` so all the design's
 * ``[data-theme="..."]`` token blocks apply.
 *
 * Default = "gruvbox". Cross-tab sync via the ``storage`` event,
 * mirroring the AuthProvider pattern.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

export type ThemeId =
  | "tokyo-night"
  | "nord"
  | "gruvbox"
  | "catppuccin"
  | "rose-pine"
  | "everforest"
  | "solarized-light";

export const THEMES: readonly { id: ThemeId; name: string }[] = [
  { id: "gruvbox", name: "Gruvbox" },
  { id: "tokyo-night", name: "Tokyo Night" },
  { id: "nord", name: "Nord" },
  { id: "catppuccin", name: "Catppuccin" },
  { id: "rose-pine", name: "Rosé Pine" },
  { id: "everforest", name: "Everforest" },
  { id: "solarized-light", name: "Solarized Light" },
];

const STORAGE_KEY = "vuzlyk.theme";
const DEFAULT_THEME: ThemeId = "gruvbox";

interface ThemeContextValue {
  theme: ThemeId;
  setTheme(t: ThemeId): void;
  themes: typeof THEMES;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  /** Override storage for tests. */
  storage?: Storage;
  /** Override initial theme for tests (skips localStorage read). */
  initial?: ThemeId;
}

export function ThemeProvider({
  children,
  storage,
  initial,
}: ThemeProviderProps): JSX.Element {
  const store = storage ?? (typeof globalThis.localStorage !== "undefined" ? globalThis.localStorage : undefined);
  const [theme, setThemeState] = useState<ThemeId>(() => {
    if (initial !== undefined) return initial;
    if (store === undefined) return DEFAULT_THEME;
    const raw = store.getItem(STORAGE_KEY);
    return isThemeId(raw) ? raw : DEFAULT_THEME;
  });

  // Apply to <html data-theme="..."> on every change.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback(
    (t: ThemeId) => {
      setThemeState(t);
      store?.setItem(STORAGE_KEY, t);
    },
    [store],
  );

  // Cross-tab sync: another tab changes the theme, update locally.
  useEffect(() => {
    if (storage !== undefined) return; // tests pass a custom storage; skip window listener
    if (typeof window === "undefined") return;
    const handler = (event: StorageEvent): void => {
      if (event.key === STORAGE_KEY && isThemeId(event.newValue)) {
        setThemeState(event.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storage]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, themes: THEMES }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}

function isThemeId(v: unknown): v is ThemeId {
  return (
    typeof v === "string" &&
    THEMES.some((t) => t.id === v)
  );
}
